import type React from "react";
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { AddCircleOutlineOutlined, Add, Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { ServiceInformationProps } from "../../../types/steps";
import type {
  DeceasedInfo,
  BookingFieldsConfig,
} from "../../../types/bookings";
import PhotoEditor from "../../../components/PhotoEditor";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import type { Venue } from "../../../types/venue";
import AddBookingFormFields from "../components/AddBookingFormFields";

const BookingServiceDetails: React.FC<ServiceInformationProps> = ({
  serviceType,
  bookingData,
  setBookingData,
}) => {
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [newVenue, setNewVenue] = useState<Venue>({
    name: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "Harare",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [venueLoading, setVenueLoading] = useState<boolean>(false);
  const [addDeceasedOpen, setAddDeceasedOpen] = useState(false);
  const [newDeceased, setNewDeceased] = useState<DeceasedInfo>({
    deceased_salutation: "",
    deceased_name: "",
    deceased_surname: "",
    second_name: "",
    nickname: "",
    gender: "",
    date_of_birth: "",
    date_of_death: "",
    photo_url: "",
    deceasedImageFile: null,
    relationship_to_deceased: "",
    religion: "",
    denomination: "",
  });
  const [deceasedList, setDeceasedList] = useState<DeceasedInfo[]>(
    bookingData.deceased_list || []
  );
  const [venueSearchTerm, setVenueSearchTerm] = useState("");

  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    if (deceasedList.length === 0) {
      const defaultDeceased: DeceasedInfo = {
        deceased_salutation: bookingData.deceased_salutation || "",
        deceased_name: bookingData.deceased_name || "",
        deceased_surname: bookingData.deceased_surname || "",
        second_name: bookingData.second_name || "",
        gender: bookingData.gender || "",
        nickname: bookingData.nickname || "",
        date_of_birth: bookingData.date_of_birth || "",
        date_of_death: bookingData.date_of_death || "",
        photo_url: bookingData.photo_url || "",
        deceasedImageFile: bookingData.deceasedImageFile || null,
        relationship_to_deceased: bookingData.relationship_to_deceased || "",
        religion: bookingData.religion || "",
        denomination: bookingData.denomination || "",
      };

      const initialList = [defaultDeceased];
      setDeceasedList(initialList);
      setBookingData((prev) => ({
        ...prev,
        deceased_list: initialList,
      }));
    }
  }, [
    bookingData.date_of_birth,
    bookingData.date_of_death,
    bookingData.deceasedImageFile,
    bookingData.deceased_name,
    bookingData.deceased_salutation,
    bookingData.deceased_surname,
    bookingData.denomination,
    bookingData.gender,
    bookingData.nickname,
    bookingData.photo_url,
    bookingData.relationship_to_deceased,
    bookingData.religion,
    bookingData.second_name,
    deceasedList.length,
    setBookingData,
  ]);

  // Fetch initial selected venues
  useEffect(() => {
    const loadSelectedVenue = async () => {
      const venueId = bookingData.venue_ids?.[0];

      if (venueId && !selectedVenue) {
        setVenueLoading(true);
        try {
          const response = await apiService.get<Venue>(
            `/api/venues/venue/${venueId}/`
          );
          setSelectedVenue(response.data);
        } catch (err: unknown) {
          console.error("Failed to fetch selected venue:", err);
          toast.error("Failed to load selected venue. Please try again.", {
            position: "top-right",
            autoClose: 5000,
            theme: "light",
          });
        } finally {
          setVenueLoading(false);
        }
      }
    };

    loadSelectedVenue();
  }, [bookingData.venue_ids, selectedVenue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (venueSearchTerm.length >= 3) {
        fetchVenues(venueSearchTerm);
      } else {
        setVenueOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [venueSearchTerm]);

  useEffect(() => {
    return () => {
      deceasedList.forEach((deceased) => {
        if (deceased.photo_url?.startsWith("blob:")) {
          URL.revokeObjectURL(deceased.photo_url);
        }
      });
    };
  }, [deceasedList]);

  const handleRemoveDeceased = (index: number) => {
    if (deceasedList.length <= 1) {
      toast.error("At least one deceased entry is required.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    const updatedList = deceasedList.filter((_, i) => i !== index);
    setDeceasedList(updatedList);
    setBookingData((prev) => ({
      ...prev,
      deceased_list: updatedList,
      deceased_salutation: updatedList[0]?.deceased_salutation || "",
      second_name: updatedList[0]?.second_name || "",
      deceased_name: updatedList[0]?.deceased_name || "",
      deceased_surname: updatedList[0]?.deceased_surname || "",
      nickname: updatedList[0]?.nickname || "",
      gender: updatedList[0]?.gender || "",
      date_of_birth: updatedList[0]?.date_of_birth || "",
      date_of_death: updatedList[0]?.date_of_death || "",
      photo_url: updatedList[0]?.photo_url || "",
      deceasedImageFile: updatedList[0]?.deceasedImageFile || null,
      relationship_to_deceased: updatedList[0]?.relationship_to_deceased || "",
      religion: updatedList[0]?.religion || "",
      denomination: updatedList[0]?.denomination || "",
    }));
  };

  const handleDeceasedChange = (
    index: number,
    field: keyof DeceasedInfo,
    value: string | File | null
  ) => {
    const updatedList = deceasedList.map((deceased, i) =>
      i === index ? { ...deceased, [field]: value } : deceased
    );
    setDeceasedList(updatedList);
    setBookingData((prev) => ({
      ...prev,
      deceased_list: updatedList,

      deceased_salutation: updatedList[0]?.deceased_salutation || "",
      deceased_name: updatedList[0]?.deceased_name || "",
      second_name: updatedList[0]?.second_name || "",
      deceased_surname: updatedList[0]?.deceased_surname || "",
      nickname: updatedList[0]?.nickname || "",
      gender: updatedList[0]?.gender || "",
      date_of_birth: updatedList[0]?.date_of_birth || "",
      date_of_death: updatedList[0]?.date_of_death || "",
      photo_url: updatedList[0]?.photo_url || "",
      deceasedImageFile: updatedList[0]?.deceasedImageFile || null,
      relationship_to_deceased: updatedList[0]?.relationship_to_deceased || "",
      religion: updatedList[0]?.religion || "",
      denomination: updatedList[0]?.denomination || "",
    }));
  };

  const handleSaveEditedImage = async (imageData: string, index?: number) => {
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File(
        [blob],
        `deceased_image_${index ?? deceasedList.length}.jpg`,
        {
          type: blob.type,
        }
      );

      if (index !== undefined) {
        const updatedList = deceasedList.map((deceased, i) =>
          i === index
            ? {
                ...deceased,
                photo_url: imageData,
                deceasedImageFile: file,
              }
            : deceased
        );
        setDeceasedList(updatedList);
        setBookingData((prev) => ({
          ...prev,
          deceased_list: updatedList,
          photo_url: updatedList[0]?.photo_url || "",
          deceasedImageFile: updatedList[0]?.deceasedImageFile || null,
          deceased_salutation: updatedList[0]?.deceased_salutation || "",
          deceased_name: updatedList[0]?.deceased_name || "",
          second_name: updatedList[0]?.second_name || "",
          deceased_surname: updatedList[0]?.deceased_surname || "",
          nickname: updatedList[0]?.nickname || "",
          gender: updatedList[0]?.gender || "",
          date_of_birth: updatedList[0]?.date_of_birth || "",
          date_of_death: updatedList[0]?.date_of_death || "",
          relationship_to_deceased:
            updatedList[0]?.relationship_to_deceased || "",
          religion: updatedList[0]?.religion || "",
          denomination: updatedList[0]?.denomination || "",
        }));
      } else {
        setNewDeceased((prev) => ({
          ...prev,
          photo_url: imageData,
          deceasedImageFile: file,
        }));
      }
    } catch (error) {
      console.error("Failed to process image:", error);
      toast.error("Failed to process image. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value } = e.target;
    if (name) {
      const deceasedFields = [
        "deceased_salutation",
        "deceased_name",
        "deceased_surname",
        "second_name",
        "gender",
        "nickname",
        "date_of_birth",
        "date_of_death",
        "religion",
        "denomination",
      ];

      if (deceasedFields.includes(name) && deceasedList.length > 0) {
        const fieldMapping: { [key: string]: keyof DeceasedInfo } = {
          deceased_salutation: "deceased_salutation",
          deceased_name: "deceased_name",
          deceased_surname: "deceased_surname",
          second_name: "second_name",
          nickname: "nickname",
          gender: "gender",
          date_of_birth: "date_of_birth",
          date_of_death: "date_of_death",
          religion: "religion",
          denomination: "denomination",
        };

        const deceasedField = fieldMapping[name];
        if (deceasedField) {
          handleDeceasedChange(0, deceasedField, value as string);
        }
      } else {
        setBookingData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleNewVenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVenue((prev) => ({ ...prev, [name]: value }));
  };

  const fetchVenues = async (search: string) => {
    setVenueLoading(true);
    try {
      const response = await apiService.get<{ results: Venue[] }>(
        `/api/venues/venue/?search=${search}`
      );
      setVenueOptions(response.data.results || []);
    } catch (err: unknown) {
      console.error("Failed to fetch venues:", err);
      toast.error("Failed to load venues. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setVenueLoading(false);
    }
  };

  const handleVenueChange = (
    event: React.SyntheticEvent,
    newValue: Venue | null
  ) => {
    setSelectedVenue(newValue);

    if (!newValue) {
      setBookingData((prevData) => ({
        ...prevData,
        venue_ids: [],
        name: "",
        address_one: "",
        address_two: "",
        city: "",
        suburb: "",
        areAddressFieldsDisabled: false,
      }));
      return;
    }

    const {
      id,
      name = "",
      address_one = "",
      address_two = "",
      city = "",
      suburb = "",
    } = newValue;

    setBookingData((prevData) => ({
      ...prevData,
      venue_ids: [String(id)],
      name,
      address_one,
      address_two,
      city,
      suburb,
      areAddressFieldsDisabled: true,
    }));
  };

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setNewVenue({
      name: "",
      address_one: "",
      address_two: "",
      suburb: "",
      city: "",
    });
  };

  const handleAddVenue = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<Venue>(
        "/api/venues/venue/",
        newVenue
      );

      setVenueOptions((prev) => [...prev, response.data]);

      setSelectedVenue(response.data);

      setBookingData((prevData) => ({
        ...prevData,
        name: response.data.name || "",
        venue_ids: [String(response.data.id)],
        address_one: response.data.address_one || "",
        address_two: response.data.address_two || "",
        city: response.data.city || "",
        suburb: response.data.suburb || "",
        areAddressFieldsDisabled: true,
      }));

      toast.success("Venue added successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      handleCloseDialog();
    } catch (err: unknown) {
      console.error("Failed to add venue:", err);
      let errorMessage = "Failed to add venue. Please try again.";
      const errorResponse = (err as { response?: { data?: { detail?: string } } })
        ?.response;
      if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeceased = () => {
    if (!newDeceased.deceased_name || !newDeceased.deceased_surname) {
      toast.error("Name and surname are required.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    const updatedList = [...deceasedList, newDeceased];
    setDeceasedList(updatedList);
    setBookingData((prev) => ({
      ...prev,
      deceased_list: updatedList,
      deceased_salutation: updatedList[0]?.deceased_salutation || "",
      second_name: updatedList[0]?.second_name || "",
      deceased_name: updatedList[0]?.deceased_name || "",
      deceased_surname: updatedList[0]?.deceased_surname || "",
      nickname: updatedList[0]?.nickname || "",
      date_of_birth: updatedList[0]?.date_of_birth || "",
      gender: updatedList[0]?.gender || "",
      date_of_death: updatedList[0]?.date_of_death || "",
      photo_url: updatedList[0]?.photo_url || "",
      deceasedImageFile: updatedList[0]?.deceasedImageFile || null,
      religion: updatedList[0]?.religion || "",
      denomination: updatedList[0]?.denomination || "",
    }));

    setNewDeceased({
      deceased_salutation: "",
      deceased_name: "",
      deceased_surname: "",
      second_name: "",
      nickname: "", // always reset to string
      gender: "",
      date_of_birth: "",
      date_of_death: "",
      photo_url: "",
      deceasedImageFile: null,
      religion: "",
      denomination: "",
      relationship_to_deceased: "",
    });
    setAddDeceasedOpen(false);
  };

  const serviceFields =
    (AddBookingFormFields as BookingFieldsConfig)[serviceType]?.serviceFields ||
    [];

  const fieldsWithSmallerGrid = [
    "deceased_salutation",
    "start_time",
    "end_time",
    "date_of_birth",
    "deceased_dod",
    "sunset",
    "date_of_service",
    "religion",
    "denomination",
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {(serviceType === "FUNERAL" || serviceType === "FUNERAL_HOME") && (
          <>
            <Grid size={{ xs: 4 }}>
              <PhotoEditor
                initialImage={
                  bookingData.deceased_list?.[0]?.person_image || null
                }
                onSave={(imageData: string) =>
                  handleSaveEditedImage(imageData, 0)
                }
                title={`Upload Deceased Image `}
              />
            </Grid>
            <Grid size={{ xs: 8 }}></Grid>
          </>
        )}

        {serviceFields.map((field) => {
          const isVenueField = field.name === "venue_name";
          const gridSize = fieldsWithSmallerGrid.includes(field.name) ? 2 : 4;

          if (isVenueField) {
            return (
              <Grid size={{ xs: 12, md: gridSize }} key={field.name}>
                <Autocomplete
                  fullWidth
                  options={venueOptions}
                  getOptionLabel={(option: Venue) => option.name || ""}
                  value={selectedVenue}
                  onChange={handleVenueChange}
                  onInputChange={(event, newInputValue) => {
                    setVenueSearchTerm(newInputValue);
                  }}
                  loading={venueLoading}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Venues"
                      variant="outlined"
                      helperText="Type at least 3 characters to search venues"
                      required={serviceType === "CONFERENCE"}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {venueLoading && (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color:
                                    reduxTheme.primary_colors?.[400] ||
                                    "#614438",
                                }}
                              />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          "& fieldset": {
                            borderColor:
                              reduxTheme.primary_colors?.[300] || "#ddd",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              reduxTheme.neutral_colors?.[400] || "#bbb",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              reduxTheme.primary_colors?.[400] || "#614438",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: reduxTheme.primary_colors?.[300] || "#666",
                        },
                        "& .MuiInputBase-input": {
                          color: reduxTheme.neutral_colors?.[100] || "#333",
                        },
                      }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => handleOpenDialog()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1,
                    color: reduxTheme.primary_colors?.[400] || "#614438",
                    "&:hover": {
                      backgroundColor:
                        reduxTheme.neutral_colors?.[800] || "#f9f9f9",
                    },
                  }}
                >
                  <AddCircleOutlineOutlined sx={{ mr: 0.5 }} />
                  <Typography
                    sx={{
                      color: reduxTheme.neutral_colors?.[100] || "#333",
                    }}
                  >
                    Add Venue
                  </Typography>
                </IconButton>
              </Grid>
            );
          }

          return (
            <>
              <Grid size={{ xs: 12, md: gridSize }} key={field.name}>
                <CustomTextField
                  fullWidth
                  select={field.type === "select"}
                  type={
                    field.type === "date"
                      ? "date"
                      : field.type === "time"
                      ? "time"
                      : "text"
                  }
                  label={field.label}
                  name={field.name}
                  disabled={field.disabled}
                  value={bookingData[field.name] || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    marginBottom: "15px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                      },
                      "&:hover fieldset": {
                        borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          reduxTheme.primary_colors?.[400] || "#614438",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: reduxTheme.primary_colors?.[300] || "#666",
                      "&.Mui-disabled": {
                        opacity: 1,
                        color: reduxTheme.primary_colors?.[300] || "#666",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: reduxTheme.neutral_colors?.[100] || "#333",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      color: "#000",
                    },
                  }}
                  InputLabelProps={
                    field.type === "date" || field.type === "time"
                      ? { shrink: true }
                      : undefined
                  }
                >
                  {field.type === "select" &&
                    field.options?.map((option, index) => {
                      const optionValue =
                        typeof option === "object"
                          ? (option as { value: string }).value
                          : option;
                      const optionLabel =
                        typeof option === "object"
                          ? (option as { label: string }).label
                          : option;
                      return (
                        <MenuItem
                          key={`${field.name}-${optionValue}-${index}`}
                          value={optionValue}
                          sx={{
                            color: reduxTheme.neutral_colors?.[100] || "#333",
                            "&:hover": {
                              backgroundColor:
                                reduxTheme.neutral_colors?.[800] || "#f9f9f9",
                            },
                          }}
                        >
                          {optionLabel}
                        </MenuItem>
                      );
                    })}
                </CustomTextField>
              </Grid>
            </>
          );
        })}
        {(serviceType === "FUNERAL" ||
          serviceType === "FUNERAL_HOME" ||
          serviceType === "EVENT") && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5">
                Additional Information (Optional)
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <CustomTextField
                fullWidth
                label="Name of Priest"
                name="preacher_name"
                value={bookingData.preacher_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                    },
                    "&:hover fieldset": {
                      borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        reduxTheme.primary_colors?.[400] || "#614438",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: reduxTheme.primary_colors?.[300] || "#666",
                  },
                  "& .MuiInputBase-input": {
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <CustomTextField
                fullWidth
                label="Name of MC"
                name="mc_name"
                value={bookingData.mc_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                    },
                    "&:hover fieldset": {
                      borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        reduxTheme.primary_colors?.[400] || "#614438",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: reduxTheme.primary_colors?.[300] || "#666",
                  },
                  "& .MuiInputBase-input": {
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <CustomTextField
                fullWidth
                label="Name of Eulogist"
                name="eulogist_name"
                value={bookingData.eulogist_name || ""}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                    },
                    "&:hover fieldset": {
                      borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        reduxTheme.primary_colors?.[400] || "#614438",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: reduxTheme.primary_colors?.[300] || "#666",
                  },
                  "& .MuiInputBase-input": {
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" sx={{ mt: 2 }}>
                Other Deceased Information
              </Typography>
            </Grid>

            {deceasedList
              .filter((_, index) => index > 0)
              .map((deceased, index) => (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[800],
                    p: 2,
                    borderRadius: 3,
                    mb: 2,
                  }}
                  key={index}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Deceased {index + 2}
                    </Typography>

                    <IconButton
                      onClick={() => handleRemoveDeceased(index + 1)}
                      aria-label="Remove deceased entry"
                    >
                      <Close />
                    </IconButton>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <PhotoEditor
                      initialImage={deceased.photo_url || null}
                      onSave={(imageData: string) =>
                        handleSaveEditedImage(imageData, index + 1)
                      }
                      title={`Upload Deceased Image ${index + 2}`}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <CustomTextField
                      label="Name"
                      name="deceased_name"
                      value={deceased.deceased_name || ""}
                      onChange={(e) =>
                        handleDeceasedChange(
                          index + 1,
                          "deceased_name",
                          e.target.value
                        )
                      }
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <CustomTextField
                      label="Name"
                      name="second_name"
                      value={deceased.second_name || ""}
                      onChange={(e) =>
                        handleDeceasedChange(
                          index + 1,
                          "second_name",
                          e.target.value
                        )
                      }
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 2 }}>
                    <CustomTextField
                      label="Date of Birth"
                      type="date"
                      name="date_of_birth"
                      value={deceased.date_of_birth || ""}
                      onChange={(e) =>
                        handleDeceasedChange(
                          index + 1,
                          "date_of_birth",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 2 }}>
                    <CustomTextField
                      label="Date of Death"
                      type="date"
                      name="date_of_death"
                      value={deceased.date_of_death || ""}
                      onChange={(e) =>
                        handleDeceasedChange(
                          index + 1,
                          "date_of_death",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              ))}

            <Grid size={{ xs: 12 }}>
              <Button
                startIcon={<Add />}
                disabled
                onClick={() => setAddDeceasedOpen(true)}
                sx={{
                  mt: 2,
                  backgroundColor: reduxTheme.primary_colors?.[800],
                  color: reduxTheme.neutral_colors?.[100] || "#fff",
                }}
              >
                Add Deceased
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      <Dialog
        open={addDeceasedOpen}
        onClose={() => setAddDeceasedOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Deceased Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <PhotoEditor
                initialImage={newDeceased.photo_url || null}
                onSave={(imageData: string) => handleSaveEditedImage(imageData)}
                title="Upload Deceased Image"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Salutation"
                select
                name="deceased_salutation"
                value={newDeceased.deceased_salutation}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    deceased_salutation: e.target.value,
                  })
                }
                fullWidth
              >
                {" "}
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="Mr">Mr</MenuItem>
                <MenuItem value="Mrs">Mrs</MenuItem>
                <MenuItem value="Ms">Ms</MenuItem>
                <MenuItem value="Miss">Miss</MenuItem>
                <MenuItem value="Dr">Dr</MenuItem>
                <MenuItem value="Prof">Prof</MenuItem>
                <MenuItem value="Rev">Rev</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </CustomTextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Name"
                name="deceased_name"
                value={newDeceased.deceased_name}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    deceased_name: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Surname"
                name="deceased_surname"
                value={newDeceased.deceased_surname}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    deceased_surname: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Nickname"
                name="nickname"
                value={newDeceased.nickname}
                onChange={(e) =>
                  setNewDeceased({ ...newDeceased, nickname: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Other Name"
                name="second_name"
                value={newDeceased.second_name}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    second_name: e.target.value,
                  })
                }
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Date of Birth"
                type="date"
                name="date_of_birth"
                value={newDeceased.date_of_birth}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    date_of_birth: e.target.value,
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomTextField
                label="Date of Death"
                type="date"
                name="date_of_death"
                value={newDeceased.date_of_death}
                onChange={(e) =>
                  setNewDeceased({
                    ...newDeceased,
                    date_of_death: e.target.value,
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddDeceasedOpen(false)}
            sx={{
              color: reduxTheme.primary_colors?.[100] || "#614438",
              borderColor: reduxTheme.primary_colors?.[100] || "#ddd",
              backgroundColor: reduxTheme.primary_colors?.[800],
              "&:hover": {
                borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                backgroundColor: reduxTheme.neutral_colors?.[800] || "#f9f9f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDeceased}
            sx={{
              backgroundColor: reduxTheme.primary_colors?.[400] || "#614438",
              color: reduxTheme.primary_colors?.[900] || "#fff",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors?.[500] || "#503629",
              },
            }}
            variant="contained"
            disabled={
              !newDeceased.deceased_name || !newDeceased.deceased_surname
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
            color: reduxTheme.neutral_colors?.[100] || "#333",
          }}
        >
          Add New Venue
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
          }}
        >
          <CustomTextField
            fullWidth
            label="Venue Name"
            name="name"
            value={newVenue.name}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Address One"
            name="address_one"
            value={newVenue.address_one}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Address Two"
            name="address_two"
            value={newVenue.address_two}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Suburb"
            name="suburb"
            value={newVenue.suburb}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="City"
            name="city"
            value={newVenue.city}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            select
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {reduxTheme.cities?.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </CustomTextField>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: reduxTheme.primary_colors?.[100] || "#614438",
              borderColor: reduxTheme.primary_colors?.[100] || "#ddd",
              backgroundColor: reduxTheme.primary_colors?.[800],
              "&:hover": {
                borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                backgroundColor: reduxTheme.neutral_colors?.[800] || "#f9f9f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddVenue}
            sx={{
              backgroundColor: reduxTheme.primary_colors?.[400] || "#614438",
              color: reduxTheme.primary_colors?.[900] || "#fff",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors?.[500] || "#503629",
              },
              "&.Mui-disabled": {
                backgroundColor: reduxTheme.neutral_colors?.[400] || "#ddd",
                color: reduxTheme.primary_colors?.[900] || "#666",
              },
            }}
            variant="contained"
            disabled={
              loading ||
              !newVenue.name ||
              !newVenue.address_one ||
              !newVenue.city
            }
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
              />
            ) : (
              "Add Venue"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingServiceDetails;
